window.Theme = window.Theme || {};

document.addEventListener('openfiltersdrawer:click', function () {
  const drawer = document.querySelector('collection-filters-drawer');
  if (drawer && typeof drawer.show === 'function') {
    drawer.show();
  }
});


Theme.querySelectorAll = function (selector, context = document) {
  return Array.from(context.querySelectorAll(selector));
};


Theme.URLParams = {
  set: function (key, value) {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    history.replaceState(null, '', url);
  },

  get: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.get(key);
  },

  delete: function (key) {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    history.replaceState(null, '', url);
  },

  has: function (key) {
    const url = new URL(window.location.href);
    return url.searchParams.has(key);
  }
};


Theme.Helpers = {
  preload_image: function (url) {
    if (!url) {
      return;
    }

    const img = new Image();
    img.src = url;
  },

  prefetch: function (url) {
    if (document.querySelector(`link[href="${url}"]`)) {
      return;
    }

    const link = document.createElement("link");

    link.rel = "prefetch";
    link.href = url;

    document.head.appendChild(link);
  },

  debounce: function (fn, delay) {
    let timeout;

    return function() {
      const context = this;
      const args = arguments;

      clearTimeout(timeout);

      timeout = setTimeout(() => {
        fn.apply(context, args);
      }, delay);
    };
  },

  throttle: function (callback, limit) {
    let lastCall = 0;

    return function () {
      const now = Date.now();

      if (now - lastCall >= limit) {
        lastCall = now;
        callback.apply(this, arguments);
      }
    };
  },

  scroll_to: function (id, offset = 80) {
    const target = document.getElementById(id);

    if (!target) {
      return;
    }

    const position = target.offsetTop - offset;

    window.scrollTo({
      top: position,
      behavior: "smooth"
    });
  }
};


Theme.Component = class extends HTMLElement {
  #elements = null;
  #data = null;
  #controller = new AbortController();
  #actionController = new AbortController();
  #observers = new Set();

  constructor() {
    super();
  }

  connectedCallback() {
    this.addActionEvents();
  }

  get data() {
    if (this.#data != null) {
      return this.#data;
    }

    const element = this.querySelector(":scope > script[type='application/json']");

    if (!element) {
      return (this.#data = {});
    } else {
      return (this.#data = JSON.parse(element.textContent));
    }
  }

  get elements() {
    if (this.#elements) {
      return this.#elements;
    }

    this.#elements = {};
    this.getElements();
    this.getElementCollections();

    return this.#elements;
  }

  getElements() {
    this.querySelectorAll("[data-element]").forEach((element) => {
      const name = element.dataset.element;
      this.defineLazySelector(name, `[data-element="${name}"]`);
    });
  }

  getElementCollections() {
    this.querySelectorAll("[data-elements]").forEach((element) => {
      const name = element.dataset.elements;

      if (!this.#elements[name]) {
        this.defineLazySelector(name, `[data-elements="${name}"]`, true);
      }
    });
  }

  defineElements(map = {}) {
    this.#elements ||= {};

    for (const [name, selector] of Object.entries(map)) {
      this.defineLazySelector(name, selector);
    }
  }

  defineElementCollections(map = {}) {
    this.#elements ||= {};

    for (const [name, selector] of Object.entries(map)) {
      this.defineLazySelector(name, selector, true);
    }
  }

  defineLazySelector(name, selector, collection = false) {
    Object.defineProperty(this.#elements, name, {
      configurable: true,
      get: () => {
        let element = null;

        if (collection) {
          element = Array.from(this.querySelectorAll(selector));
        } else {
          element = this.querySelector(selector);
        }

        Object.defineProperty(this.#elements, name, { value: element });

        return element;
      }
    });
  }

  addActionEvents() {
    const elements = this.querySelectorAll("[data-action]");

    elements.forEach((element) => {
      const [event, method] = element.dataset.action.split(":");
      const detail = JSON.parse(element.dataset.detail || "{}");

      this.addEvent(event, element, () => {
        this[method](detail, element);
      });
    });
  }

  resetActionEvents() {
    this.#actionController.abort();
    this.#actionController = new AbortController();

    this.addActionEvents();
  }

  addEvent(type, target, listener) {
    this.addEventListener(type, (event) => {
      if (this.closestElement(event.target, target)) {
        listener.call(this, event);
        event.stopPropagation();
      }
    }, { signal: this.#actionController.signal });
  }

  addDirectEvent(type, target, listener) {
    target.addEventListener(type, (event) => {
      listener.call(this, event);
    }, { signal: this.#controller.signal });
  }

  closestElement(starting, matching) {
    let current = starting;

    while (current) {
      if (current === matching) {
        return current;
      }

      current = current.parentElement;
    }

    return null;
  }

  intersectionObserver(fn, options = { threshold: 0.25 }) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        fn(entry, observer);
      });
    }, options);

    this.#observers.add(observer);
    observer.observe(this);

    return observer;
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#controller = new AbortController();

    this.#actionController.abort();
    this.#actionController = new AbortController();

    this.#observers.forEach(observer => observer.disconnect());
    this.#observers.clear();
  }
}


Theme.Drawer = class extends Theme.Component {
  #returnFocus = null;
  #inertElements = [];

  connectedCallback() {
    super.connectedCallback();

    this.addDirectEvent("keydown", document, (event) => {
      if (event.key === "Escape" && this.isOpen) {
        this.hide();
      }
    });
  }

  get isOpen() {
    return this.classList.contains("state--active");
  }

  get panel() {
    return this.querySelector("[data-drawer-panel]");
  }

  get triggers() {
    return Theme.querySelectorAll(`[aria-controls="${this.id}"]`);
  }

  show() {
    if (this.isOpen) {
      this.focusPanel();
      return;
    }

    this.#returnFocus = document.activeElement;
    this.removeAttribute("inert");
    this.setBackgroundInert(true);
    this.setExpanded(true);

    requestAnimationFrame(() => {
      this.classList.add("state--active");
      this.focusAfterTransition();
    });
  }

  hide() {
    if (!this.isOpen) {
      return;
    }

    this.classList.remove("state--active");
    this.setAttribute("inert", "");
    this.setBackgroundInert(false);
    this.setExpanded(false);

    this.#returnFocus?.focus?.({ preventScroll: true });
    this.#returnFocus = null;
  }

  toggle() {
    if (this.isOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  focusPanel() {
    this.panel?.focus({ preventScroll: true });
  }

  focusAfterTransition() {
    const panel = this.panel;

    if (!panel) {
      return;
    }

    const duration = parseFloat(getComputedStyle(panel).transitionDuration) || 0;

    if (duration === 0) {
      this.focusPanel();
      return;
    }

    const onEnd = (event) => {
      if (event.target !== panel) {
        return;
      }

      panel.removeEventListener("transitionend", onEnd);
      this.focusPanel();
    };

    panel.addEventListener("transitionend", onEnd);
  }

  setExpanded(state) {
    this.triggers.forEach((trigger) => {
      trigger.setAttribute("aria-expanded", state ? "true" : "false");
    });
  }

  setBackgroundInert(state) {
    this.#inertElements.forEach((element) => element.removeAttribute("inert"));
    this.#inertElements = [];

    if (!state) {
      return;
    }

    const section = this.closest(".shopify-section") || this;

    this.#inertElements = Array.from(document.body.children).filter((element) => {
      return element !== section && element.tagName !== "SCRIPT" && !element.hasAttribute("inert");
    });

    this.#inertElements.forEach((element) => element.setAttribute("inert", ""));
  }
};


Theme.ComponentCollection = class {
  #elements = null;

  constructor(selector) {
    this.define(selector);
  }

  define(selector) {
    Object.defineProperty(this, "elements", {
      get: () => {
        return this.#elements || (this.#elements = Theme.querySelectorAll(selector));
      }
    });
  }
};

Theme.ComponentCollections = {};

function alignProductCardsHeight(root) {
  const scope = root && typeof root.querySelectorAll === 'function' ? root : document;
  const cards = Array.from(scope.querySelectorAll('product-card'));
  if (!cards.length) return;

  const rows = {};
  cards.forEach(card => {
    const info = card.querySelector('[data-product-info]');
    if (info) info.style.minHeight = 'auto';
    const top = card.offsetTop;
    rows[top] ||= [];
    rows[top].push({ info });
  });

  Object.values(rows).forEach(row => {
    const maxInfoHeight = Math.max(...row.map(x => x.info ? x.info.offsetHeight : 0));
    row.forEach(({ info }) => {
      if (info) {
        info.style.minHeight = `${maxInfoHeight}px`;
        info.classList.add('is-ready');
      }
    });
  });
}
  
document.addEventListener('DOMContentLoaded', () => alignProductCardsHeight());
window.addEventListener('resize', Theme.Helpers.debounce(() => alignProductCardsHeight(), 200));
